import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  ASSIGN_PROJECTS_TO_ROLES_URL,
  CREATE_ROLE,
  GET_COMPANIES,
  GET_USER_ROLES,
  UPDATE_ROLE,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'ManageRole',
  mixins: [alert_mixin],
  data: () => ({
    roles: [],
    projects: [],
    is_loading: false,
    projects_loading: false,
    creating_role: false,
    updating_role: false,
    assigning_projects: false,
    create_form: {
      role_name: '',
      role_description: ''
    },
    edit_form: {
      role_id: null,
      role_name: '',
      role_description: ''
    },
    current_role: null,
    is_managing_role: false,
    active_role_tab: 'assignments',
    selected_project_ids: [],
    original_project_ids: []
  }),
  computed: {
    active_roles_count() {
      return this.roles.filter((role) => role.is_active !== false).length;
    },
    described_roles_count() {
      return this.roles.filter((role) => String(role.description || '').trim()).length;
    },
    inactive_roles_count() {
      return this.roles.filter((role) => role.is_active === false).length;
    }
  },
  mounted() {
    this.fetch_roles();
    this.fetch_projects();
  },
  methods: {
    parse_api_data(payload) {
      if (typeof payload === 'string') {
        let parsed_payload = {};

        try {
          parsed_payload = JSON.parse(payload);
        } catch {
          parsed_payload = {};
        }

        return parsed_payload;
      }

      const normalized_payload = payload || {};
      return normalized_payload;
    },
    map_role(role = {}) {
      const role_project_ids = Array.isArray(role.project_ids) ? role.project_ids : [];
      const role_projects = Array.isArray(role.projects) ? role.projects : [];
      const normalized_project_ids = role_projects
        .map((project) => project?.id || project?.project_id || project)
        .filter((project_id) => project_id != null && project_id !== '');
      const assigned_project_ids = role_project_ids.length ? role_project_ids : normalized_project_ids;
      const normalized_role = {
        id: role.id || role.role_id || role.pk || null,
        name: String(role.role || role.name || 'Unnamed Role').trim(),
        description: String(role.description || role.role_description || '').trim(),
        is_active: role.is_active !== false,
        assigned_project_ids: assigned_project_ids.map(String)
      };

      return normalized_role;
    },
    parse_error_message(error, fallback) {
      const response_data = this.parse_api_data(error?.response?.data);
      return (
        response_data.message ||
        response_data.data ||
        error.message ||
        fallback
      );
    },

    reset_create_form() {
      this.create_form = {
        role_name: '',
        role_description: ''
      };
    },

    reset_edit_form() {
      this.edit_form = {
        role_id: null,
        role_name: '',
        role_description: ''
      };
    },

    async fetch_roles() {
      this.is_loading = true;

      try {
        const response = await axios.get(GET_USER_ROLES, get_request_config());
        const response_data = this.parse_api_data(response.data);
        const items = Array.isArray(response_data.data) ? response_data.data : [];

        this.roles = items
          .map((role) => this.map_role(role))
          .filter((role) => role.id != null || role.name);
      } catch {
        this.roles = [];
        this.show_error('Failed to load roles.');
      } finally {
        this.is_loading = false;
      }
    },
    async fetch_projects() {
      this.projects_loading = true;

      try {
        const response = await axios.get(GET_COMPANIES, get_request_config());
        const response_data = this.parse_api_data(response.data);
        const items = response_data.data || response_data.projects || [];

        this.projects = Array.isArray(items)
          ? items.map((project) => ({
              id: project.id,
              name: project.project_name || project.name || `Project ${project.id || ''}`,
              description: project.project_description || ''
            }))
          : [];
      } catch {
        this.projects = [];
      } finally {
        this.projects_loading = false;
      }
    },
    open_create_role_modal() {
      this.reset_create_form();
    },
    open_edit_role_modal(role) {
      this.edit_form = {
        role_id: role?.id || null,
        role_name: String(role?.name || '').trim(),
        role_description: String(role?.description || '').trim()
      };

      const modal_element = document.getElementById('edit_role_modal');
      if (modal_element) {
        const modal_instance = window.bootstrap.Modal.getInstance(modal_element) || new window.bootstrap.Modal(modal_element);
        modal_instance.show();
      }
    },
    async create_role() {
      const role_name = String(this.create_form.role_name || '').trim();

      if (!role_name) {
        this.show_error('Role name cannot be empty.');
        return;
      }

      this.creating_role = true;
      const loader = await this.show_loader('Creating role...');

      try {
        const payload = {
          role_name,
          role_description: String(this.create_form.role_description || '').trim()
        };

        const response = await axios.post(CREATE_ROLE, payload, get_request_config());
        const response_data = this.parse_api_data(response.data);

        if (response_data?.status && String(response_data.status).toLowerCase() !== 'success') {
          throw new Error(response_data.message || 'Role creation failed.');
        }

        this.close_modal('create_role_modal');
        this.reset_create_form();
        await this.fetch_roles();
        this.show_success(response_data.message || 'Role created successfully.');
      } catch (error) {
        this.show_error(this.parse_error_message(error, 'Failed to create role.'));
      } finally {
        this.creating_role = false;
        this.close_loader(loader);
      }
    },
    async update_role() {
      const role_id = Number(this.edit_form.role_id);
      const role_name = String(this.edit_form.role_name || '').trim();

      if (!Number.isInteger(role_id) || role_id <= 0) {
        this.show_error('Role id is required.');
        return;
      }

      if (!role_name) {
        this.show_error('Role name cannot be empty.');
        return;
      }

      this.updating_role = true;
      const loader = await this.show_loader('Updating role...');

      try {
        const payload = {
          role_id,
          role_name,
          role_description: String(this.edit_form.role_description || '').trim()
        };

        const response = await axios.post(UPDATE_ROLE, payload, get_request_config());
        const response_data = this.parse_api_data(response.data);

        if (response_data?.status && String(response_data.status).toLowerCase() !== 'success') {
          throw new Error(response_data.message || 'Role update failed.');
        }

        this.close_modal('edit_role_modal');
        this.reset_edit_form();
        await this.fetch_roles();

        if (this.current_role && String(this.current_role.id) === String(role_id)) {
          const updated_role = this.roles.find((role) => String(role.id) === String(role_id));
          if (updated_role) {
            this.current_role = {
              ...updated_role
            };
          }
        }

        this.show_success(response_data.message || 'Role updated successfully.');
      } catch (error) {
        this.show_error(this.parse_error_message(error, 'Failed to update role.'));
      } finally {
        this.updating_role = false;
        this.close_loader(loader);
      }
    },
    manage_role(role) {
      const assigned_project_ids = Array.isArray(role?.assigned_project_ids)
        ? role.assigned_project_ids.map(String)
        : [];

      this.is_managing_role = true;
      this.current_role = { ...role };
      this.active_role_tab = 'assignments';
      this.selected_project_ids = assigned_project_ids;
      this.original_project_ids = assigned_project_ids;
    },
    close_manage_role() {
      this.is_managing_role = false;
      this.current_role = null;
      this.active_role_tab = 'assignments';
      this.selected_project_ids = [];
      this.original_project_ids = [];
    },
    toggle_role_tab(tab) {
      this.active_role_tab = tab;
    },
    toggle_assignment(project_id) {
      const normalized_project_id = String(project_id);
      const selected_ids = new Set(this.selected_project_ids.map(String));

      if (selected_ids.has(normalized_project_id)) {
        selected_ids.delete(normalized_project_id);
      } else {
        selected_ids.add(normalized_project_id);
      }

      this.selected_project_ids = Array.from(selected_ids);
    },
    is_project_selected(project_id) {
      return this.selected_project_ids.map(String).includes(String(project_id));
    },
    async assign_projects_to_role() {
      if (!this.current_role) {
        this.show_error('Select a role before managing assignments.');
        return;
      }

      if (!this.current_role.id) {
        return;
      }

      this.assigning_projects = true;
      const loader = await this.show_loader('Saving role assignments...');

      try {
        const payload = {
          role_id: Number(this.current_role.id),
          project_ids: this.selected_project_ids
            .map((project_id) => Number(project_id))
            .filter((project_id) => Number.isInteger(project_id) && project_id > 0)
        };

        const response = await axios.post(
          ASSIGN_PROJECTS_TO_ROLES_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_api_data(response.data);

        if (response_data?.status && String(response_data.status).toLowerCase() !== 'success') {
          throw new Error(response_data.message || 'Failed to update project assignments.');
        }

        const assigned_project_ids = payload.project_ids.map(String);
        this.original_project_ids = assigned_project_ids;
        this.selected_project_ids = assigned_project_ids;
        this.current_role = {
          ...this.current_role,
          assigned_project_ids
        };
        this.roles = this.roles.map((role) =>
          String(role.id) === String(this.current_role.id)
            ? {
              ...role,
              assigned_project_ids
            }
            : role
        );

        this.show_success(response_data.message || 'Project assignments updated successfully');
      } catch (error) {
        this.show_error(this.parse_error_message(error, 'Failed to update project assignments.'));
      } finally {
        this.assigning_projects = false;
        this.close_loader(loader);
      }
    }
  }
};
